// JouwLootje.jsx — /lootjes/jouw-lootje/:lootjeToken
// Deelnemer ziet zijn lootje + AI-cadeausuggesties

import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Events } from "../analytics";

const BUDGET_LABELS = { "15": "tot €15", "30": "€15–€30", "60": "€30–€60", "100": "€60–€100", "100plus": "€100+" };
const INTEREST_LABELS = {
  muziek: "muziek 🎵", sport: "sport 🏃", koken: "koken 🍳", reizen: "reizen ✈️",
  lezen: "lezen 📚", gaming: "gaming 🎮", natuur: "natuur 🌿", film: "film & series 🎬",
  mode: "mode 👗", tech: "tech 💻", kunst: "kunst 🎨", wellness: "wellness 🧘",
};

export default function JouwLootje() {
  const { lootjeToken } = useParams();
  const [lootje, setLootje] = useState(null);
  const [fout, setFout] = useState(null);
  const [producten, setProducten] = useState([]);
  const [aiLaden, setAiLaden] = useState(false);

  useEffect(() => {
    try {
      const data = JSON.parse(atob(lootjeToken.replace(/-/g, "+").replace(/_/g, "/")));
      setLootje(data);
      Events.track("Lootje geopend");
      laadSuggesties(data);
    } catch {
      setFout("Ongeldig lootje. Vraag de organisator om een nieuwe link.");
    }
  }, [lootjeToken]);

  async function laadSuggesties(data) {
    setAiLaden(true);
    try {
      const res = await fetch("/.netlify/functions/send-suggestions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          naam: data.ontvangerNaam,
          ageGroup: "dertig", // default — we vragen geen leeftijd bij lootjes
          gender: "nvt",
          relation: "vriend",
          interests: data.ontvangerInteresses || [],
          interestsExtra: data.ontvangerInterestsExtra || "",
          lifeEvent: "geen",
          budget: data.budget,
          eventType: `Lootje ${data.groepNaam}`,
          eventDatum: data.datum || new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
          alleenSuggesties: true,
        }),
      });
      const json = await res.json();
      setProducten(json.producten || []);
      Events.track("Lootje suggesties geladen");
    } catch {
      // Stil falen — suggesties zijn bonus
    } finally {
      setAiLaden(false);
    }
  }

  if (fout) return (
    <main className="main">
      <div className="card fi" style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>😕</div>
        <p style={{ color: "#8A7B73" }}>{fout}</p>
      </div>
    </main>
  );

  if (!lootje) return (
    <main className="main">
      <div className="card fi" style={{ textAlign: "center", padding: 40 }}>
        <div className="ai-spinner" style={{ margin: "0 auto 16px" }} />
      </div>
    </main>
  );

  return (
    <main className="main">
      <div className="card fi">
        {/* Reveal */}
        <div style={{
          background: "linear-gradient(135deg,#C9856A,#A86349)",
          borderRadius: 20, padding: 28, marginBottom: 22, textAlign: "center",
        }}>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 6 }}>
            {lootje.groepNaam}
          </div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.8)", marginBottom: 12 }}>
            {lootje.trekkerNaam}, jij hebt getrokken…
          </div>
          <div style={{ fontSize: 42, fontWeight: 800, color: "white", letterSpacing: -1, marginBottom: 8 }}>
            🎁 {lootje.ontvangerNaam}
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }}>
            Budget: {BUDGET_LABELS[lootje.budget]}
            {lootje.datum ? ` · ${new Date(lootje.datum).toLocaleDateString("nl-NL", { day: "numeric", month: "long" })}` : ""}
          </div>
        </div>

        {/* Interesses ontvanger */}
        {lootje.ontvangerInteresses?.length > 0 && (
          <div style={{
            background: "#EDF3EF", border: "1px solid rgba(61,90,76,0.15)",
            borderRadius: 14, padding: "14px 16px", marginBottom: 20,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#2C2420", marginBottom: 8 }}>
              {lootje.ontvangerNaam} houdt van:
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {lootje.ontvangerInteresses.map(i => (
                <span key={i} style={{
                  background: "white", border: "1px solid #EDE3D9",
                  borderRadius: 50, padding: "4px 12px", fontSize: 13, color: "#2C2420",
                }}>
                  {INTEREST_LABELS[i] || i}
                </span>
              ))}
            </div>
            {lootje.ontvangerInterestsExtra && (
              <div style={{ fontSize: 12, color: "#8A7B73", marginTop: 8, fontStyle: "italic" }}>
                "{lootje.ontvangerInterestsExtra}"
              </div>
            )}
          </div>
        )}

        {/* Suggesties */}
        <div className="flabel">Cadeausuggesties op maat</div>
        {aiLaden ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 0", color: "#8A7B73", fontSize: 14 }}>
            <div className="ai-spinner" />
            Suggesties ophalen…
          </div>
        ) : producten.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
            {producten.map((p, i) => (
              <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
                onClick={() => Events.PRODUCT_KLIK(p.naam, p.winkel)}
                style={{ textDecoration: "none" }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 12,
                  background: "#FFFCF9", border: "1.5px solid #EDE3D9",
                  borderRadius: 14, padding: "14px 16px",
                  transition: "border-color 0.15s",
                }}>
                  <span style={{ fontSize: 26, flexShrink: 0 }}>{p.emoji}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#2C2420", marginBottom: 2, lineHeight: 1.3 }}>
                      {p.naam}
                    </div>
                    <div style={{ fontSize: 12, color: "#8A7B73" }}>{p.winkel}</div>
                  </div>
                  <div style={{ flexShrink: 0, textAlign: "right" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#C9856A" }}>{p.prijs}</div>
                    <div style={{ fontSize: 11, color: "#C9856A", marginTop: 2 }}>Bekijk →</div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div style={{ padding: "12px 0 20px", color: "#8A7B73", fontSize: 14 }}>
            Geen suggesties beschikbaar. Kijk eens op{" "}
            <a href="https://www.hema.nl" target="_blank" rel="noopener noreferrer" style={{ color: "#C9856A" }}>HEMA</a>,{" "}
            <a href="https://www.bol.com" target="_blank" rel="noopener noreferrer" style={{ color: "#C9856A" }}>bol.com</a> of{" "}
            <a href="https://www.coolblue.nl" target="_blank" rel="noopener noreferrer" style={{ color: "#C9856A" }}>Coolblue</a>.
          </div>
        )}

        {/* Virale CTA */}
        <div style={{
          background: "#FDEEE8", border: "1.5px solid #EDE3D9",
          borderRadius: 16, padding: 18, textAlign: "center",
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2C2420", marginBottom: 6 }}>
            Nooit meer een verjaardag vergeten?
          </div>
          <div style={{ fontSize: 13, color: "#8A7B73", marginBottom: 14, lineHeight: 1.6 }}>
            Givn stuurt je tijdig een herinnering met suggesties — gratis, altijd.
          </div>
          <Link to="/" onClick={() => Events.VIRAL_CONVERSIE()} style={{
            display: "inline-block", background: "#C9856A", color: "white",
            textDecoration: "none", padding: "12px 24px", borderRadius: 50,
            fontWeight: 700, fontSize: 14,
          }}>
            Start gratis met Givn →
          </Link>
        </div>
      </div>
    </main>
  );
}
